import { api } from "@/lib/api";
import { tarihSaat } from "@/lib/veri";
import { Band, Buton, Etiket, Kutu, Kv } from "@/components/parcalar";
import { cevir } from "@/lib/dil";
import { donusturucuRozeti, gecenMetni, kisaTarihSaat, saat, sirali, sureMetni, type Donusum } from "@/lib/mrb-toplu";
import { raporAdi } from "@/lib/rapor-toplu";

/**
 * D-061: söküm raporunun dönüşüm işi ekranda (söküm listesi ve eski rapor kaydı ortak kullanır). Sunucudaki `IsGorunumu`:
 * `donusum` null = iş yok; `siraNo` ve `siraTs` yalnız sıradayken, tutma alanları dönüştürülürken, hata alanları hatada doludur.
 */
export interface IsGorunumu {
  donusum: Donusum | null;
  siraNo: number | null;
  siraTs: string | null;
  tutmaBaslangic: string | null;
  tutmaBitis: string | null;
  donusumHata: string | null;
  donusumHataTs: string | null;
}

/**
 * "Disassembly report converter" kutusunun verisi; dönüştürücü o an bir MRB ile meşgulse `donusturulen.tur` "mrb" gelir.
 * D-065: Disposition ya da Rework raporuyla meşgulse tür o rapordur ve riseri `rn`dedir.
 * D-068: Assembly raporuyla meşgulse tür "assembly"dir ve riseri `rn`dedir. D-074 Faz 2: hidrotest sertifikasıyla meşgulse tür
 * "hidrotest"tir ve `ad` okuma işi kodudur ("HT-…"). D-074 Faz 2 · PR 9: NDT raporunda tür "ndt", `ad` "NDTR-…". D-074 Faz 2 · PR 10: weld map'te
 * tür "weldmap", `ad` "WMAP-…". D-074 Faz 2 · PR 11: PWHT grafiğinde tür "pwht", `ad` "PWHT-…". D-074 Faz 3 · PR 14: NDT sertifikasında tür "ndt_personel",
 * `ad` "NDTC-…". D-074 Faz 3 · PR 15: kaynakçı belgesinde tür "kaynakci_wpq", `ad` "WPQR-…". D-074 Faz 3 · PR 16: kalibrasyon sertifikasında tür
 * "alet_kalibrasyon", `ad` "CAL-…". D-074 Faz 3 · PR 17: sarf belgesinde tür "sarf_belgesi", `ad` "CONS-…". D-074 Faz 3 · PR 18: WPS belgesinde tür
 * "wps_belgesi", `ad` "PROC-…". D-074 Faz 4 · PR 20: packing list'te tür "teslim_listesi", `ad` "PL-…". D-074 Faz 4 · PR 21: malzeme sertifikasında tür
 * "malzeme_sertifikasi", `ad` "MTC-…". D-074 Faz 4 · PR 22: CoC'de tür "uygunluk_sertifikasi", `ad` "CC-…". D-074 Faz 4 · PR 23: PO'da tür "satin_alma", `ad` "PUR-…".
 */
export interface SokumDonusturucu {
  sonBaglanti: string | null;
  anahtarAd: string | null;
  aktifAnahtarVar: boolean;
  donusturulen:
    | { tur: "sokum"; rn: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "mrb"; sn: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "disposition" | "rework"; rn: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "assembly"; rn: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "hidrotest"; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "ndt"; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "weldmap"; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "pwht"; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "ndt_personel"; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "kaynakci_wpq"; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "alet_kalibrasyon"; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "sarf_belgesi"; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "wps_belgesi"; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "teslim_listesi"; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "malzeme_sertifikasi"; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "uygunluk_sertifikasi"; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | { tur: "satin_alma"; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null }
    | null;
  sirada: number;
  hatali: number;
}

/**
 * Kuyruk işlemi: sıraya ekle ("Add to queue", hatadan ya da nottan "Retry") ya da sıradan çıkar.
 * U-056 (D-061): `sabitMrbSn` yalnız "MRB arrived · SN differs" notundaki "Retry" ile gider; basan kişinin onayladığı MRB'nin SN'idir
 * ve sunucu onu işe yazar. Verilmezse gövde eskisiyle aynıdır.
 */
export const sokumSiraIslemi = (id: string, islem: "ekle" | "cikar", sabitMrbSn?: string) => api.post(`/sokum/${id}/sira`, sabitMrbSn ? { islem, sabitMrbSn } : { islem });

const TON = { sirada: "neutral", donusturuluyor: "info", hata: "bad" } as const;

/** İşin durum etiketi: queued · {n} · converting · failed; iş yoksa "none". */
export function KuyrukEtiketi({ k, className }: { k: IsGorunumu; className?: string }) {
  if (!k.donusum) return <Etiket tone="warn" className={className}>{cevir("none")}</Etiket>;
  const ad = k.donusum === "sirada" ? (k.siraNo ? cevir("queued · {0}", sirali(k.siraNo)) : cevir("queued")) : k.donusum === "donusturuluyor" ? cevir("converting") : cevir("failed");
  return <Etiket tone={TON[k.donusum]} className={className}>{ad}</Etiket>;
}

/** Hatanın zamanı ve tek cümle sebebi ("08/10, 13:20 · Form could not be read: …"); ikisi de yoksa boş. */
export const hataAyrintisi = (k: IsGorunumu) => [k.donusumHataTs ? kisaTarihSaat(k.donusumHataTs) : null, k.donusumHata ? cevir(k.donusumHata) : null].filter(Boolean).join(" · ");

/**
 * Söküm girdisinin dönüşüm durumu ve düğmesi: queued · {n} (+ Remove from queue; bekleyen kayıtta yok, riser excel'i bekler) ·
 * converting (başlama saati, düğme yok) · failed (+ Retry, altında zaman ve sebep) · none (+ Add to queue, sunuluyorsa).
 * `dugmesiz`: yalnız etiket ve ayrıntı (kayıt sayfasında düğmeler ayrı satırdadır).
 */
export function SokumGirdiDurumu({ k, bekliyor, eklenebilir, islem, mesgul, dugmesiz }: { k: IsGorunumu; bekliyor: boolean; eklenebilir: boolean; islem: (islem: "ekle" | "cikar") => void; mesgul?: boolean; dugmesiz?: boolean }) {
  const etiket = <KuyrukEtiketi k={k} />;
  const not = (metin: string) => <span className="text-xs text-ink-3">{metin}</span>;
  // Listede satır tıklaması kaydı açar; düğme açmasın.
  const dugme = (ad: string, i: "ekle" | "cikar") => (dugmesiz ? null : <> <span onClick={(e) => e.stopPropagation()}><Buton kucuk islem="sokum" mesgul={mesgul} onClick={() => islem(i)}>{ad}</Buton></span></>);
  switch (k.donusum) {
    case "sirada":
      return <>{etiket}{!bekliyor && dugme(cevir("Remove from queue"), "cikar")}</>;
    case "donusturuluyor":
      return <>{etiket}{k.tutmaBaslangic && <> {not(cevir("since {0}", saat(k.tutmaBaslangic)))}</>}</>;
    case "hata": {
      const ayrinti = hataAyrintisi(k);
      return (
        <>
          {etiket}{dugme(cevir("Retry"), "ekle")}
          {ayrinti && <div className="mt-1 max-w-lg text-xs text-bad">{ayrinti}</div>}
        </>
      );
    }
    default:
      return <>{etiket}{eklenebilir && dugme(cevir("Add to queue"), "ekle")}</>;
  }
}

/**
 * D-061 "Disassembly report converter" (MRB intake'teki "MRB converter" kutusunun eşi): son bağlantı (sunucu saatiyle), anahtar,
 * şu an dönüştürülen ve tutma süresi, sırada ve hatalı söküm raporu sayısı. Dönüştürücü tektir: bir MRB ile meşgulse o görünür.
 */
export function SokumDonusturucuKutusu({ d, simdi }: { d: SokumDonusturucu; simdi: number }) {
  const r = donusturucuRozeti(d, simdi);
  const rozet =
    r.tur === "bagli" ? <Etiket tone="ok">{cevir("connected")}</Etiket>
    : r.tur === "kopuk" ? <Etiket tone={r.uyari ? "warn" : "neutral"}>{cevir("not connected for {0}", sureMetni(r.gecen ?? 0))}</Etiket>
    : <Etiket>{cevir("not connected yet")}</Etiket>;
  const t = d.donusturulen;
  const tutma = t ? [t.tutmaBaslangic ? cevir("since {0}", saat(t.tutmaBaslangic)) : null, t.tutmaBitis ? cevir("held until {0}", saat(t.tutmaBitis)) : null].filter(Boolean).join(" · ") : "";
  return (
    <Kutu baslik={cevir("Disassembly report converter")} aksiyon={rozet}>
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kv ad={cevir("Last connected")}>{d.sonBaglanti ? <><b>{tarihSaat(d.sonBaglanti)}</b> <span className="text-ink-3">· {gecenMetni(r.gecen ?? 0)}</span></> : cevir("never")}</Kv>
        <Kv ad={cevir("Key")}>{d.anahtarAd ? <>{d.anahtarAd} <span className="text-ink-3">· {cevir("created by System Administrator")}</span></> : "—"}</Kv>
        <Kv ad={cevir("Now converting")}>
          {t ? <><b className="font-mono">{t.tur === "sokum" ? t.rn : t.tur === "mrb" ? `MRB ${t.sn}` : t.tur === "assembly" ? cevir("Assembly report {0}", t.rn) : t.tur === "hidrotest" ? cevir("Hydrotest certificate {0}", t.ad) : t.tur === "ndt" ? cevir("NDT report {0}", t.ad) : t.tur === "weldmap" ? cevir("Weld map {0}", t.ad) : t.tur === "pwht" ? cevir("PWHT chart {0}", t.ad) : t.tur === "ndt_personel" ? cevir("NDT certificate {0}", t.ad) : t.tur === "kaynakci_wpq" ? cevir("Welder document {0}", t.ad) : t.tur === "alet_kalibrasyon" ? cevir("Calibration certificate {0}", t.ad) : t.tur === "sarf_belgesi" ? cevir("Consumable document {0}", t.ad) : t.tur === "wps_belgesi" ? cevir("WPS document {0}", t.ad) : t.tur === "teslim_listesi" ? cevir("Packing list {0}", t.ad) : t.tur === "malzeme_sertifikasi" ? cevir("Material certificate {0}", t.ad) : t.tur === "uygunluk_sertifikasi" ? cevir("Certificate of conformity {0}", t.ad) : t.tur === "satin_alma" ? cevir("Purchase order {0}", t.ad) : raporAdi(t.tur, t.rn)}</b>{tutma && <> <span className="text-ink-3">· {tutma}</span></>}</> : "—"}
        </Kv>
        <Kv ad={cevir("Waiting")}><b className="tnum">{d.sirada}</b> {cevir("queued")} <span className="text-ink-3">·</span> <b className="tnum">{d.hatali}</b> {cevir("failed")}</Kv>
      </div>
      {!d.aktifAnahtarVar && <Band tip="not" className="mt-3">{cevir("No active converter key. A System Administrator creates one in Admin.")}</Band>}
      {r.uyari && <Band tip="not" className="mt-3">{cevir("The converter has not connected for {0} and {1} Disassembly reports are waiting. The converter computer may be off or asleep.", sureMetni(r.gecen ?? 0), d.sirada)}</Band>}
      <Band tip="bilgi" className="mt-3">{cevir("The converter runs on a computer outside OASIS. While that computer is on, it checks the queue every 15 minutes and converts one document at a time; Disassembly reports are taken before MRBs, because risers wait for them. It reads the scanned form with the disassembly engine and delivers the disassembly input excel. The excel is read into the record right away, with the same checks as a manual upload, and a riser that was waiting moves to the Field list. An excel that fails the checks or comes with an engine warning is not read and the record shows as failed, with the reason. A disassembly input uploaded by hand is never replaced by the converter. If the computer sleeps during a conversion, the report goes back to the queue when its hold time runs out.")}</Band>
    </Kutu>
  );
}
